/**
 * Keeps off-screen Swiper slides out of the tab order and out of the
 * accessibility tree.
 *
 * Swiper's A11y module labels slides and wires up the navigation buttons, but
 * it never touches `tabindex` or `aria-hidden` — there is not one occurrence of
 * either in swiper/modules/a11y.mjs. Every link inside every clipped slide
 * therefore stays tabbable: on the homepage slider that is thirteen invisible
 * product links a keyboard user must walk through, each one taking focus to
 * something they cannot see (WCAG 2.4.3 Focus Order, 2.4.7 Focus Visible).
 *
 * "Visible" here is a set of slides, not a single slide. With slidesPerView: 6
 * all six on-screen slides must stay reachable, and on mobile (slidesPerView
 * 1.35 with centeredSlides) the partially-shown neighbour must too. Swiper
 * computes exactly that set in updateSlidesProgress and marks it with
 * `swiper-slide-visible` — but only when `watchSlidesProgress` is true, which
 * is why every caller has to pass it.
 *
 * Loop mode moves the real slide nodes rather than cloning them, so the visible
 * set has to be recomputed after `loopFix`, and the slide currently holding
 * focus must never be suppressed.
 *
 * Usage, after constructing a Swiper with `watchSlidesProgress: true`:
 *   window.enhanceSwiperKeyboardAccessibility(swiperInstance);
 */
const VISIBLE_SLIDE_CLASS = 'swiper-slide-visible';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  '[contenteditable="true"]',
].join(', ');

const SUPPRESSED_ATTRIBUTE = 'data-swiper-focus-suppressed';
const ORIGINAL_TAB_INDEX_ATTRIBUTE = 'data-swiper-original-tab-index';

// Events after which the visible set may have changed. `setTranslate` is
// deliberately absent: it fires on every frame of a drag, and the tab order
// being momentarily stale mid-gesture does not matter — `transitionEnd` covers
// the settled state.
const RESYNCHRONIZE_EVENTS = [
  'slideChangeTransitionEnd',
  'transitionEnd',
  'loopFix',
  'resize',
  'breakpoint',
  'update',
  'observerUpdate',
];

function suppressSlideFocusability(slideElement) {
  slideElement.setAttribute('aria-hidden', 'true');

  slideElement.querySelectorAll(FOCUSABLE_SELECTOR).forEach((focusableElement) => {
    if (focusableElement.hasAttribute(SUPPRESSED_ATTRIBUTE)) return;

    // Remember any tabindex the markup set, so restoring does not silently
    // promote an intentionally unfocusable element into the tab order.
    if (focusableElement.hasAttribute('tabindex')) {
      focusableElement.setAttribute(ORIGINAL_TAB_INDEX_ATTRIBUTE, focusableElement.getAttribute('tabindex'));
    }

    focusableElement.setAttribute('tabindex', '-1');
    focusableElement.setAttribute(SUPPRESSED_ATTRIBUTE, 'true');
  });
}

function restoreSlideFocusability(slideElement) {
  slideElement.setAttribute('aria-hidden', 'false');

  slideElement.querySelectorAll(`[${SUPPRESSED_ATTRIBUTE}]`).forEach((focusableElement) => {
    const originalTabIndex = focusableElement.getAttribute(ORIGINAL_TAB_INDEX_ATTRIBUTE);

    if (originalTabIndex === null) {
      focusableElement.removeAttribute('tabindex');
    } else {
      focusableElement.setAttribute('tabindex', originalTabIndex);
      focusableElement.removeAttribute(ORIGINAL_TAB_INDEX_ATTRIBUTE);
    }

    focusableElement.removeAttribute(SUPPRESSED_ATTRIBUTE);
  });
}

/**
 * Whether a keyboard visitor can still bring an off-screen slide into view.
 *
 * Taking clipped slides out of the tab order is only safe if there is a way
 * back to them. A section whose navigation arrows are switched off, with no
 * loop, has none — suppressing there would put whole slides permanently out of
 * reach, which is a worse failure (WCAG 2.1.1 Keyboard) than a long tab
 * sequence. So when navigation is unreachable we leave every slide focusable
 * and accept the extra tab stops.
 *
 * offsetParent is null when an ancestor is display:none, which covers both the
 * theme setting hiding the arrows and Swiper's own swiper-button-lock. The
 * lock case is harmless: it only applies when every slide already fits, so
 * there is nothing to suppress either way.
 */
function hasReachableNavigation(swiperInstance) {
  const nextElement = swiperInstance.navigation?.nextEl;
  if (!nextElement) return false;

  const navigationButtons = Array.isArray(nextElement) ? nextElement : [nextElement];
  return navigationButtons.some((button) => button && button.offsetParent !== null);
}

function synchronizeSlideFocusability(swiperInstance) {
  if (!swiperInstance || swiperInstance.destroyed) return;

  // Without watchSlidesProgress the visible class is never applied, so every
  // slide would look off-screen and the whole carousel would be hidden from
  // keyboard users. Bail out and leave the markup alone instead.
  if (!swiperInstance.params.watchSlidesProgress) return;

  const navigationReachable = hasReachableNavigation(swiperInstance);

  swiperInstance.slides.forEach((slideElement) => {
    // aria-hidden on an ancestor of the focused element is itself a violation
    // (axe flags it as aria-hidden-focus), and pulling tabindex out from under
    // a link the user is standing on strands them. The slide holding focus is
    // always left reachable, and re-evaluated once focus leaves it.
    const holdsFocus = slideElement.contains(document.activeElement);

    if (holdsFocus || !navigationReachable || slideElement.classList.contains(VISIBLE_SLIDE_CLASS)) {
      restoreSlideFocusability(slideElement);
    } else {
      suppressSlideFocusability(slideElement);
    }
  });
}

window.enhanceSwiperKeyboardAccessibility = function enhanceSwiperKeyboardAccessibility(swiperInstance) {
  if (!swiperInstance || !swiperInstance.el) return;

  let scheduledAnimationFrame = null;

  // Several of the events below fire together on a single interaction; coalesce
  // them so the DOM is walked once per frame rather than once per event.
  const scheduleSynchronization = () => {
    if (scheduledAnimationFrame !== null) return;

    scheduledAnimationFrame = requestAnimationFrame(() => {
      scheduledAnimationFrame = null;
      synchronizeSlideFocusability(swiperInstance);
    });
  };

  RESYNCHRONIZE_EVENTS.forEach((eventName) => swiperInstance.on(eventName, scheduleSynchronization));

  // Re-evaluates the slide that was exempted while it held focus.
  swiperInstance.el.addEventListener('focusout', scheduleSynchronization);

  swiperInstance.on('destroy', () => {
    if (scheduledAnimationFrame !== null) cancelAnimationFrame(scheduledAnimationFrame);
    swiperInstance.el.removeEventListener('focusout', scheduleSynchronization);
  });

  synchronizeSlideFocusability(swiperInstance);
};
